'use client';

import { useEffect, useRef } from 'react';

const noop = () => {};

export type ScrollBoundary = `top` | `bottom`;

export type UseScrollBoundaryOpts = {
  /**
   * this margin above and below the scrollHeight boundary determines when the callback is fired
   * can be a fixed number or a function which receives viewportHeight and returns a margin
   */
  margin?: number | ((viewportHeight: number, scrollHeight: number) => number);
  /** called when the boundary is crossed */
  onBoundary?: (boundary: ScrollBoundary) => void;
  /** called when the scrollTop has changed. provides a ratio (0-1) value for the scroll relative to the top */
  onScrollChanged?: (scrollTop: number, ratio: number) => void;
};

export const useScrollBoundary = (
  scrollTop: number,
  scrollHeight: number,
  viewportHeight: number,
  opts?: UseScrollBoundaryOpts
) => {
  const lastRatio = useRef(0);
  const lastBoundary = useRef<ScrollBoundary>(null);

  const {
    margin: incomingMargin = 100,
    onBoundary = noop,
    onScrollChanged: onRatioChanged = noop,
  } = opts || {};

  const margin =
    typeof incomingMargin === 'function'
      ? incomingMargin(viewportHeight, scrollHeight)
      : incomingMargin;

  useEffect(() => {
    const scrollRatio = scrollTop / (scrollHeight - viewportHeight) || 0;
    if (lastRatio.current !== scrollRatio) {
      lastRatio.current = scrollRatio;
      onRatioChanged(scrollTop, scrollRatio);
    }

    if (scrollTop + viewportHeight >= scrollHeight - margin) {
      if (lastBoundary.current !== `bottom`) {
        onBoundary(`bottom`);
        lastBoundary.current = `bottom`;
      }
    } else if (scrollTop <= margin) {
      if (lastBoundary.current !== `top`) {
        onBoundary(`top`);
        lastBoundary.current = `top`;
      }
    } else {
      lastBoundary.current = null;
    }
  });
};
